import { useApolloClient } from '@vue/apollo-composable'
import type { Ref } from 'vue'
import { watch } from 'vue'

import { getT } from '@/lib/ai/i18n'
import bus, { eventNames } from '@/lib/eventBus'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import type { PendingAction } from '@/stores/aiAssistant/types'

import COMPANY from '../queries/CompanyData.gql'
import UPDATE_COMPANY_ADDRESS from '../queries/UpdateCompanyAddress.gql'
import UPDATE_COMPANY_AMBASSADOR_DATA from '../queries/UpdateCompanyAmbassadorData.gql'
import UPDATE_COMPANY_COMMISSION from '../queries/UpdateCompanyCommission.gql'
import UPDATE_COMPANY_LINKS from '../queries/UpdateCompanyLinks.gql'
import UPDATE_COMPANY_NAME from '../queries/UpdateCompanyName.gql'
import UPDATE_COMPANY_REGION from '../queries/UpdateCompanyRegion.gql'
import UPDATE_COMPANY_SLOGAN from '../queries/UpdateCompanySlogan.gql'
import UPDATE_COMPANY_THEME from '../queries/UpdateCompanyTheme.gql'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CompanyData = Record<string, any>

interface UseSettingsAiActionHandlerOptions {
  /** Reactive ref kept in sync with the Apollo company object. */
  companyRef: Ref<CompanyData | null>
  /** Called after a successful mutation so the context can be refreshed. */
  onMutationSuccess?: () => void
}

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/

/**
 * Watches for confirmed AI pending actions that target settings mutations and
 * dispatches them to the correct GraphQL mutation.
 *
 * Must be called from a component's setup() so useApolloClient() resolves correctly.
 */
export function useSettingsAiActionHandler({
  companyRef,
  onMutationSuccess,
}: UseSettingsAiActionHandlerOptions) {
  const aiStore = useAiAssistantStore()
  const { resolveClient } = useApolloClient()

  /** Re-fetches CompanyData so all child settings components re-render with fresh data. */
  const refetchCompany = (companyId: string) => [{ query: COMPANY, variables: { id: companyId } }]

  const SETTINGS_MUTATIONS = new Set([
    'updateCompanyName',
    'updateCompanyAddress',
    'updateCompanyLinks',
    'updateCompanyTheme',
    'updateCompanyAmbassadorData',
    'updateCompanySlogan',
    'updateCompanyCommission',
    'updateCompanyRegion',
  ])

  const isSettingsAction = (mutation: string): boolean => SETTINGS_MUTATIONS.has(mutation)

  watch(
    () => aiStore.pendingAction,
    async (action) => {
      if (!action || action.status !== 'confirmed') return
      if (!isSettingsAction(action.mutation)) return

      const t = getT()

      try {
        await dispatch(action)

        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: t('ai-assistant.responses.done', { label: action.label }),
        })

        onMutationSuccess?.()
      } catch (err) {
        console.error('[AI Settings] Action handler failed:', err)
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: err instanceof Error ? err.message : String(err),
        })
      } finally {
        aiStore.pendingAction = null
      }
    },
    { deep: true }
  )

  async function dispatch(action: PendingAction): Promise<void> {
    const client = resolveClient()
    const c = companyRef.value
    if (!c) throw new Error('[AI Settings] Company data is not loaded yet.')

    switch (action.mutation) {
      case 'updateCompanyName': {
        await client.mutate({
          mutation: UPDATE_COMPANY_NAME,
          variables: { input: { companyId: c.id, name: String(action.proposedValue) } },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompanyAddress': {
        // action.field is e.g. 'address.street' — take the leaf segment
        const leafField = action.field.split('.').pop() as string
        const existingAddress = c.address ?? {}
        await client.mutate({
          mutation: UPDATE_COMPANY_ADDRESS,
          variables: {
            input: {
              companyId: c.id,
              address: {
                street: existingAddress.street ?? null,
                zip: existingAddress.zip ?? null,
                city: existingAddress.city ?? null,
                country: existingAddress.country ?? null,
                [leafField]: String(action.proposedValue),
              },
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompanyLinks': {
        // action.field is e.g. 'links.website' — take the leaf segment
        const leafField = action.field.split('.').pop() as string
        const existingLinks = c.links ?? {}
        await client.mutate({
          mutation: UPDATE_COMPANY_LINKS,
          variables: {
            input: {
              companyId: c.id,
              website: existingLinks.website ?? null,
              imprint: existingLinks.imprint ?? null,
              privacy: existingLinks.privacy ?? null,
              about: existingLinks.about ?? null,
              [leafField]: String(action.proposedValue),
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompanyTheme': {
        const colorKey = action.field.split('.').pop() as 'primary' | 'secondary'
        if (!colorKey || !['primary', 'secondary'].includes(colorKey)) {
          throw new Error(`Unknown colour field: ${action.field}`)
        }
        const newValue = String(action.proposedValue)
        if (!HEX_PATTERN.test(newValue)) {
          throw new Error(
            `Invalid hex colour "${newValue}" — must be a 6-digit hex code like #FF5733.`
          )
        }
        const existingColors = c.theme?.colors ?? {}
        await client.mutate({
          mutation: UPDATE_COMPANY_THEME,
          variables: {
            input: {
              companyId: c.id,
              colors: {
                primary: existingColors.primary ?? '#000000',
                secondary: existingColors.secondary ?? '#000000',
                [colorKey]: newValue,
              },
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompanyAmbassadorData': {
        // action.field is e.g. 'ambassador.email' — take the leaf segment
        const leafField = action.field.split('.').pop() as string
        const amb = c.ambassador ?? {}
        await client.mutate({
          mutation: UPDATE_COMPANY_AMBASSADOR_DATA,
          variables: {
            input: {
              companyId: c.id,
              ambassador: {
                name: amb.name ?? null,
                email: amb.email ?? null,
                phone: amb.phone ?? null,
                [leafField]: String(action.proposedValue),
              },
            },
          },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompanySlogan': {
        await client.mutate({
          mutation: UPDATE_COMPANY_SLOGAN,
          variables: { input: { companyId: c.id, slogan: String(action.proposedValue) } },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompanyCommission': {
        const value = Number(action.proposedValue)
        if (isNaN(value) || value < 0 || value > 100) {
          throw new Error(
            `Commission must be a number between 0 and 100. Got: ${action.proposedValue}`
          )
        }
        await client.mutate({
          mutation: UPDATE_COMPANY_COMMISSION,
          variables: { input: { companyId: c.id, commission: value } },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      case 'updateCompanyRegion': {
        await client.mutate({
          mutation: UPDATE_COMPANY_REGION,
          variables: { input: { companyId: c.id, regionName: String(action.proposedValue) } },
          refetchQueries: refetchCompany(c.id),
        })
        break
      }

      default:
        console.warn(`[AI Settings] No handler for mutation: "${action.mutation}"`)
    }
  }
}
